import type { Schema } from "@/shared/components/Form/types";
import {
  TagTypesAutocompleteQuery,
  TagTypeQuery,
} from "@/partner/entities/tags";
import { PlatformPagesAutocompleteQuery } from "@/partner/entities/platformPages";
import type { TagTypeQueryType } from "@/types/graphql/partner.generated";
import type { TFunction } from "i18next";

/**
 * `initialTypeId` нужен ровно для одного: предупредить, что при смене типа
 * значения полей прежнего типа будут удалены (спека §4). Значения хранятся
 * по имени поля, у другого типа имена свои, поэтому сервер их не примет.
 */
export const getTagFormSchema = (
  t: TFunction,
  options: {
    initialTypeId?: string | null;
    /** Слот картинки, открытый первым: основная картинка типа тега */
    defaultOrientation?: "horizontal" | "vertical";
  } = {}
): Schema => ({
  _main: {
    type: "block",
    hideHeader: true,
    xs: 12,
    md: 8,
    subschema: {
      title: {
        type: "text",
        label: t("tags.form.titleLabel"),
        required: true,
        xs: 12,
        fullWidth: true,
      },
      typeId: {
        type: "autocomplete",
        label: t("tags.form.typeLabel"),
        xs: 12,
        fullWidth: true,
        query: TagTypesAutocompleteQuery,
        helperText: ({ allValues }) => {
          const changed =
            Boolean(options.initialTypeId) &&
            allValues?.typeId !== options.initialTypeId;
          const hasValues =
            Object.keys(allValues?.customFieldValues ?? {}).length > 0;
          return changed && hasValues ? t("tags.form.typeChangeWarning") : "";
        },
      },
      description: {
        type: "text",
        label: t("tags.form.descriptionLabel"),
        multiline: true,
        rows: 4,
        xs: 12,
        fullWidth: true,
      },
      customFieldValues: {
        type: "customFields",
        label: t("tags.form.customFieldsLabel"),
        query: TagTypeQuery,
        queryVariables: ({ values }) => ({ id: values?.typeId }),
        getSchema: (data: TagTypeQueryType | undefined) =>
          (data?.tagType?.customFields ?? []).map((field) => ({
            type: field.type,
            name: field.name,
            label: field.label,
            required: field.required ?? false,
            multiple: field.multiple ?? false,
            placeholder: field.placeholder ?? undefined,
            helperText: field.helperText ?? undefined,
            options: (field.options ?? []).map((option) => ({
              value: option?.value ?? "",
              label: option?.label ?? "",
            })),
          })),
        xs: 12,
      },
      platformPageId: {
        type: "autocomplete",
        label: t("tags.form.platformPageLabel"),
        helperText: t("tags.form.platformPageHelper"),
        xs: 12,
        fullWidth: true,
        query: PlatformPagesAutocompleteQuery,
      },
    },
  },
  _right: {
    type: "block",
    hideHeader: true,
    xs: 12,
    md: 4,
    subschema: {
      image: {
        // Без orientation BannerField рисует оба слота с переключателем
        // 16:9 / 2:3, как превью видео. defaultOrientation открывает
        // первым слот, который у типа тега основной (портрет для людей).
        type: "banner",
        label: t("tags.form.imagesLabel"),
        helperText: t("tags.form.imagesHelper"),
        defaultOrientation: options.defaultOrientation,
        xs: 12,
        fullWidth: true,
      },
    },
  },
});
